/**
 * StudyPickerModal — configure and launch an AI medical-history review for one
 * lead: WHAT to read (evidence sources) and WHICH studies to check it against.
 *
 * Sources (owner spec): the recruiter scopes the review to any of medical
 * records (with a per-DOCUMENT subset), patient communications, and forms.
 * Default = medical records only (all documents); communications + forms OFF.
 * Patient identity (sex/DOB) and recruiter-entered profile facts are ALWAYS
 * included by the server regardless — the toggles gate only the AI-analyzed
 * buckets, so the copy says so.
 *
 * Cost: POST /api/crm/contacts/:id/eligibility-review-estimate (READ-ONLY) is
 * (re)called, debounced, as the selection changes — it returns the lead's
 * medical-record documents (the checklist), which toggles are available, and a
 * per-study + total dollar estimate for the CURRENT selection. The server stays
 * the source of truth for cost and for the fail-closed budget gate.
 *
 * On open it also fetches GET /eligibility-review-readiness: studies without a
 * confirmed criteria set render DISABLED so a mixed selection can't 409 the
 * whole batch. Submits POST /contacts/:id/eligibility-reviews {study_ids,
 * sources}; a 409 CRITERIA_NOT_CONFIRMED / 402 BUDGET_EXCEEDED / other 4xx are
 * surfaced inline exactly as before.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight, FileText, Loader2, MessageSquare, ClipboardList, Sparkles, X } from 'lucide-react';
import api from '../../../services/api';
import { StudyOption, studyInternalLabel } from '../../types';

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

const fmtUsd = (n: unknown): string => {
  const v = Number(n);
  return Number.isFinite(v) ? `$${v.toFixed(2)}` : String(n);
};

/** A lead medical-record document from the estimate endpoint. */
interface EstimateDoc {
  id: string;
  filename: string | null;
  page_count: number;
  source: string | null;
  created_at: string;
  extraction_cached: boolean;
}
interface StudyEstimate {
  study_id: string;
  label: string | null;
  ready: boolean;
  reason?: string;
  criteria_count: number;
  estimated_cost: number | null;
}
interface EstimateResponse {
  selection: {
    medical_records: { enabled: boolean; document_ids: string[] };
    communications: { enabled: boolean };
    forms: { enabled: boolean };
  };
  availability: { medical_records: boolean; communications: boolean; forms: boolean };
  documents: EstimateDoc[];
  estimates: StudyEstimate[];
  total_estimated_cost: number;
  pages_total: number;
  budget: {
    enabled: boolean | null;
    per_review_cap: number | null;
    monthly_cap: number | null;
    month_to_date: number | null;
  };
}

interface Props {
  contactId: string;
  /** The contact's Considering study — pre-checked when present in the list. */
  primaryStudyId?: string | null;
  onClose: () => void;
  /** Reviews accepted (202) — parent refetches its list and closes the modal. */
  onStarted: () => void;
}

const fmtDate = (iso: string): string => {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '';
  }
};

export default function StudyPickerModal({ contactId, primaryStudyId = null, onClose, onStarted }: Props) {
  const [studies, setStudies] = useState<StudyOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Study ids the server said lack a confirmed review-criteria set (409). */
  const [needsSetup, setNeedsSetup] = useState<string[]>([]);
  /**
   * Per-study confirmed-criteria readiness (GET /eligibility-review-readiness).
   * null = readiness unknown (endpoint unavailable/failed) — fail open and let
   * the server's 409 backstop catch unready studies at submit time.
   */
  const [readiness, setReadiness] = useState<Map<string, boolean> | null>(null);

  // ---- Source selection ----------------------------------------------------
  const [mrEnabled, setMrEnabled] = useState(true);          // medical records (default ON)
  const [commsEnabled, setCommsEnabled] = useState(false);   // patient communications (default OFF)
  const [formsEnabled, setFormsEnabled] = useState(false);   // forms & questionnaires (default OFF)
  const [docs, setDocs] = useState<EstimateDoc[]>([]);
  const [docSel, setDocSel] = useState<Set<string>>(new Set());
  const [docsExpanded, setDocsExpanded] = useState(false);
  const [availability, setAvailability] = useState<EstimateResponse['availability']>({
    medical_records: true, communications: true, forms: true,
  });

  // ---- Live estimate -------------------------------------------------------
  const [estimate, setEstimate] = useState<EstimateResponse | null>(null);
  const [estimating, setEstimating] = useState(false);
  const [estimateError, setEstimateError] = useState<string | null>(null);
  /** True once the first estimate has populated the document list — before then
   *  we omit document_ids so the server defaults to ALL docs (a failed initial
   *  load must never silently mean "zero records"). */
  const initializedRef = useRef(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get('/api/crm/studies');
        if (!alive) return;
        const all: StudyOption[] = res.data?.studies || [];
        // Real CRM studies only; recruiting ones (plus the Considering study
        // even if paused, so the default check always has its row).
        const usable = all.filter((s) =>
          !!s.id && s.source === 'crm' && !s.archived_at &&
          (s.status === 'recruiting' || s.id === primaryStudyId));
        setStudies(usable);

        // Which of these studies have CONFIRMED review criteria? Unready ones
        // render disabled + unchecked instead of 409ing the whole batch.
        let ready: Map<string, boolean> | null = null;
        if (usable.length > 0) {
          try {
            const ids = usable.map((s) => s.id!).join(',');
            const r = await api.get(`/api/crm/eligibility-review-readiness?study_ids=${encodeURIComponent(ids)}`);
            if (!alive) return;
            const list: any[] = Array.isArray(r.data?.readiness) ? r.data.readiness : [];
            ready = new Map(list
              .filter((x) => typeof x?.study_id === 'string')
              .map((x) => [x.study_id as string, !!x.confirmed]));
          } catch {
            ready = null; // fail open — the POST's 409 remains the backstop
          }
        }
        if (!alive) return;
        setReadiness(ready);

        // Pre-check the Considering study unless it is known-unready.
        if (
          primaryStudyId &&
          usable.some((s) => s.id === primaryStudyId) &&
          !(ready && ready.get(primaryStudyId) === false)
        ) {
          setSelected(new Set([primaryStudyId]));
        }
        setLoadError(null);
      } catch (e: any) {
        if (alive) setLoadError(errMsg(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [primaryStudyId]);

  // Escape cancels (unless a submit is in flight).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, busy]);

  const needsSetupSet = useMemo(() => new Set(needsSetup), [needsSetup]);

  /** Not ready for review: known-unconfirmed criteria (readiness fetch or 409). */
  const isUnready = useCallback(
    (studyId: string): boolean =>
      needsSetupSet.has(studyId) || (readiness != null && readiness.get(studyId) === false),
    [needsSetupSet, readiness]);

  /** The selection that would actually be submitted (ready studies only). */
  const readySelectedIds = useMemo(
    () => Array.from(selected).filter((sid) => !isUnready(sid)),
    [selected, isUnready]);

  const toggle = (studyId: string) => {
    if (isUnready(studyId)) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(studyId)) next.delete(studyId);
      else next.add(studyId);
      return next;
    });
  };

  /** The `sources` payload for the estimate + start calls. Before the first
   *  estimate returns (initializedRef false) we OMIT document_ids so the server
   *  defaults to all docs — pinning an (empty) list then would exclude every
   *  record. */
  const buildSources = useCallback(() => {
    const mr: { enabled: boolean; document_ids?: string[] } = { enabled: mrEnabled };
    if (mrEnabled && initializedRef.current) mr.document_ids = Array.from(docSel);
    return {
      medical_records: mr,
      communications: { enabled: commsEnabled },
      forms: { enabled: formsEnabled },
    };
  }, [mrEnabled, docSel, commsEnabled, formsEnabled]);

  // Stable signature so the debounced estimate only refires on a real change.
  const sourceSig = useMemo(
    () => JSON.stringify({
      ids: [...readySelectedIds].sort(),
      mr: mrEnabled,
      docs: [...docSel].sort(),
      comms: commsEnabled,
      forms: formsEnabled,
    }),
    [readySelectedIds, mrEnabled, docSel, commsEnabled, formsEnabled]);

  // Debounced live estimate. Runs after studies finish loading (so the initial
  // considering-study pre-check is included) and on every selection change.
  useEffect(() => {
    if (loading) return;
    let alive = true;
    const t = setTimeout(async () => {
      setEstimating(true);
      setEstimateError(null);
      try {
        const res = await api.post(`/api/crm/contacts/${contactId}/eligibility-review-estimate`, {
          study_ids: readySelectedIds,
          sources: buildSources(),
        });
        if (!alive) return;
        const data: EstimateResponse = res.data;
        if (!initializedRef.current) {
          initializedRef.current = true;
          setDocs(Array.isArray(data.documents) ? data.documents : []);
          setDocSel(new Set((data.selection?.medical_records?.document_ids) || []));
          if (data.availability) {
            setAvailability(data.availability);
            // A source the server can't actually run is force-off.
            if (data.availability.communications === false) setCommsEnabled(false);
          }
        }
        setEstimate(data);
      } catch (e: any) {
        if (alive) setEstimateError(errMsg(e));
      } finally {
        if (alive) setEstimating(false);
      }
    }, initializedRef.current ? 300 : 0);
    return () => { alive = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceSig, loading, contactId]);

  const allDocsSelected = docs.length > 0 && docs.every((d) => docSel.has(d.id));
  const someDocsSelected = docs.some((d) => docSel.has(d.id));
  const selectedPages = useMemo(
    () => docs.filter((d) => docSel.has(d.id)).reduce((s, d) => s + (d.page_count || 0), 0),
    [docs, docSel]);

  const toggleDoc = (docId: string) => {
    setDocSel((prev) => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId);
      else next.add(docId);
      return next;
    });
  };
  const setAllDocs = (on: boolean) => setDocSel(on ? new Set(docs.map((d) => d.id)) : new Set());

  /** Any ready study priced above the per-review cap — the cost lever is the
   *  record selection, so we say so. */
  const overCap = useMemo(() => {
    const cap = estimate?.budget?.per_review_cap;
    if (cap == null || !estimate) return false;
    return estimate.estimates.some((e) => e.ready && (e.estimated_cost || 0) > cap);
  }, [estimate]);

  const submit = async () => {
    if (readySelectedIds.length === 0 || busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/crm/contacts/${contactId}/eligibility-reviews`, {
        study_ids: readySelectedIds,
        sources: buildSources(),
      });
      onStarted();
    } catch (e: any) {
      const status = e?.response?.status;
      const data = e?.response?.data || {};
      if (status === 409 && data.error === 'CRITERIA_NOT_CONFIRMED') {
        // Which studies need setup? Tolerate id-array or object-array shapes.
        const raw = data.study_ids || data.studies || [];
        const ids: string[] = (Array.isArray(raw) ? raw : [])
          .map((s: any) => (typeof s === 'string' ? s : s?.id || s?.study_id))
          .filter(Boolean);
        setNeedsSetup(ids);
        // Uncheck the offenders so a resubmit only sends ready studies.
        if (ids.length > 0) {
          setSelected((prev) => {
            const next = new Set(prev);
            ids.forEach((sid) => next.delete(sid));
            return next;
          });
        }
        const names = ids
          .map((sid) => {
            const s = studies.find((x) => x.id === sid);
            return s ? studyInternalLabel(s) : null;
          })
          .filter((n): n is string => !!n);
        setError(names.length > 0
          ? `Review criteria are not confirmed for ${names.join(', ')} — set up review criteria on the study page, then try again.`
          : 'Some selected studies do not have confirmed review criteria yet — set up review criteria on the study page, then try again.');
      } else if (status === 402) {
        const bits = [data.detail || 'The AI review budget would be exceeded.'];
        if (data.estimate != null) bits.push(`Estimated cost: ${fmtUsd(data.estimate)}.`);
        if (data.cap != null) bits.push(`Cap: ${fmtUsd(data.cap)}.`);
        setError(bits.join(' '));
      } else {
        // Any other failure (incl. non-criteria 409s like a merged contact):
        // show the server's own detail verbatim, never the criteria message.
        setError(errMsg(e));
      }
    } finally {
      setBusy(false);
    }
  };

  const totalCost = estimate?.total_estimated_cost ?? null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={() => { if (!busy) onClose(); }}
      role="presentation"
    >
      <div
        className="flex max-h-[90vh] w-full max-w-md flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-gray-800"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Conduct AI medical history review"
      >
        <div className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-700">
          <h2 className="inline-flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
            <Sparkles className="h-4 w-4 text-primary-600 dark:text-primary-400" />
            AI medical history review
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
            className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg text-gray-500 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200 sm:min-h-0 sm:min-w-0 sm:p-1.5"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Choose what the AI reads and which studies to check it against. It compares the
            selected evidence to each study's confirmed review criteria.
          </p>

          {(loadError || error) && (
            <div role="alert" className="mt-3 rounded-lg border border-red-200 bg-red-50 p-2.5 text-xs text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
              {loadError || error}
            </div>
          )}

          {/* ---- What to review (sources) ---- */}
          <div className="mt-4">
            <p className="mb-1.5 text-xs font-medium text-gray-600 dark:text-gray-300">What to review</p>
            <div className="space-y-1">
              {/* Medical records */}
              <div className={`rounded-lg border transition-colors duration-150 ${
                mrEnabled ? 'border-primary-300 bg-primary-50/50 dark:border-primary-700 dark:bg-primary-900/10' : 'border-gray-200 dark:border-gray-700'
              }`}>
                <div className="flex items-center gap-2.5 p-2.5">
                  <input
                    id="src-mr"
                    type="checkbox"
                    checked={mrEnabled}
                    onChange={(e) => {
                      const on = e.target.checked;
                      setMrEnabled(on);
                      if (on && docSel.size === 0 && docs.length > 0) setAllDocs(true);
                    }}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                  />
                  <FileText className="h-4 w-4 shrink-0 text-gray-400" />
                  <label htmlFor="src-mr" className="min-w-0 flex-1 cursor-pointer">
                    <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">Medical records</span>
                    <span className="block text-[11px] text-gray-500 dark:text-gray-400">
                      {docs.length === 0
                        ? 'Uploaded & faxed documents'
                        : mrEnabled
                          ? `${docSel.size} of ${docs.length} record${docs.length === 1 ? '' : 's'} · ${selectedPages} page${selectedPages === 1 ? '' : 's'}`
                          : `${docs.length} record${docs.length === 1 ? '' : 's'} available`}
                    </span>
                  </label>
                  {mrEnabled && docs.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setDocsExpanded((v) => !v)}
                      className="inline-flex items-center gap-0.5 rounded-md px-1.5 py-1 text-[11px] font-medium text-primary-700 hover:bg-primary-100/60 dark:text-primary-300 dark:hover:bg-primary-900/30"
                    >
                      {docsExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                      Choose
                    </button>
                  )}
                </div>

                {mrEnabled && docsExpanded && docs.length > 0 && (
                  <div className="border-t border-primary-200/70 px-2.5 py-2 dark:border-primary-800/50">
                    <div className="mb-1.5 flex items-center gap-3 text-[11px]">
                      <button type="button" onClick={() => setAllDocs(true)} disabled={allDocsSelected}
                        className="font-medium text-primary-700 hover:underline disabled:text-gray-400 disabled:no-underline dark:text-primary-300">
                        Select all
                      </button>
                      <button type="button" onClick={() => setAllDocs(false)} disabled={!someDocsSelected}
                        className="font-medium text-primary-700 hover:underline disabled:text-gray-400 disabled:no-underline dark:text-primary-300">
                        Clear
                      </button>
                    </div>
                    <ul className="max-h-40 space-y-0.5 overflow-y-auto">
                      {docs.map((d) => (
                        <li key={d.id}>
                          <label className="flex cursor-pointer items-start gap-2 rounded-md p-1.5 hover:bg-white/70 dark:hover:bg-gray-800/50">
                            <input
                              type="checkbox"
                              checked={docSel.has(d.id)}
                              onChange={() => toggleDoc(d.id)}
                              className="mt-0.5 h-3.5 w-3.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                            />
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-xs text-gray-800 dark:text-gray-200" title={d.filename || 'Untitled record'}>
                                {d.filename || 'Untitled record'}
                              </span>
                              <span className="flex flex-wrap items-center gap-1.5 text-[10px] text-gray-500 dark:text-gray-400">
                                <span>{d.page_count} pg</span>
                                {d.created_at && <span>· {fmtDate(d.created_at)}</span>}
                                {d.extraction_cached && (
                                  <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                                    already analyzed · no extra cost
                                  </span>
                                )}
                              </span>
                            </span>
                          </label>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Patient communications */}
              <SourceToggle
                id="src-comms"
                icon={<MessageSquare className="h-4 w-4 shrink-0 text-gray-400" />}
                title="Patient communications"
                subtitle="Texts, emails, calls & notes"
                checked={commsEnabled}
                disabled={availability.communications === false}
                disabledHint="Not available on this account"
                onChange={setCommsEnabled}
              />

              {/* Forms & questionnaires */}
              <SourceToggle
                id="src-forms"
                icon={<ClipboardList className="h-4 w-4 shrink-0 text-gray-400" />}
                title="Forms & questionnaires"
                subtitle="Landing & prescreen answers"
                checked={formsEnabled}
                onChange={setFormsEnabled}
              />
            </div>
            <p className="mt-1.5 text-[11px] leading-snug text-gray-400 dark:text-gray-500">
              Patient identity (sex/DOB) and recruiter-entered profile facts are always included.
            </p>
          </div>

          {/* ---- Studies ---- */}
          <div className="mt-4">
            <p className="mb-1.5 text-xs font-medium text-gray-600 dark:text-gray-300">
              Review against <span className="font-normal text-gray-400">(pick one or more studies)</span>
            </p>
            {loading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
              </div>
            ) : studies.length === 0 ? (
              <p className="py-2 text-sm text-gray-500 dark:text-gray-400">
                No recruiting studies found. Add a study under Studies first.
              </p>
            ) : (
              <ul className="space-y-1">
                {studies.map((s) => {
                  const id = s.id!;
                  const unready = isUnready(id);
                  const est = estimate?.estimates.find((e) => e.study_id === id);
                  const showCost = !unready && selected.has(id) && est?.ready && est.estimated_cost != null;
                  return (
                    <li key={id}>
                      <label
                        className={`flex min-h-[44px] items-start gap-2.5 rounded-lg border p-2.5 transition-colors duration-150 ${
                          unready
                            ? 'cursor-not-allowed border-gray-200 bg-gray-50 opacity-80 dark:border-gray-700 dark:bg-gray-800/60'
                            : selected.has(id)
                              ? 'cursor-pointer border-primary-300 bg-primary-50/60 dark:border-primary-700 dark:bg-primary-900/10'
                              : 'cursor-pointer border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700/40'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={!unready && selected.has(id)}
                          disabled={unready}
                          onChange={() => toggle(id)}
                          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700"
                        />
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center justify-between gap-2">
                            <span className="block truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                              {studyInternalLabel(s)}
                            </span>
                            {showCost && (
                              <span className="shrink-0 text-xs font-medium text-gray-500 dark:text-gray-400">
                                ~{fmtUsd(est!.estimated_cost)}
                              </span>
                            )}
                          </span>
                          <span className="mt-0.5 flex flex-wrap items-center gap-1.5">
                            {s.id === primaryStudyId && (
                              <span className="inline-flex items-center rounded-full bg-primary-100 px-1.5 py-0.5 text-[10px] font-medium text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                                Considering
                              </span>
                            )}
                            {s.status && s.status !== 'recruiting' && (
                              <span className="inline-flex items-center rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                                {s.status}
                              </span>
                            )}
                          </span>
                          {unready && (
                            <span className="mt-1 flex items-start gap-1 text-[11px] text-amber-700 dark:text-amber-400">
                              <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                              Review criteria not confirmed — set up on the study page.
                            </span>
                          )}
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        {/* ---- Footer: cost + actions ---- */}
        <div className="border-t border-gray-100 p-4 dark:border-gray-700">
          <div className="mb-2.5 flex min-h-[1.25rem] items-center justify-between text-sm">
            <span className="text-gray-500 dark:text-gray-400">Estimated cost</span>
            <span className="inline-flex items-center gap-1.5 font-semibold text-gray-900 dark:text-gray-100">
              {estimating && <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />}
              {readySelectedIds.length === 0
                ? <span className="font-normal text-gray-400">Select a study</span>
                : totalCost != null
                  ? fmtUsd(totalCost)
                  : <span className="font-normal text-gray-400">—</span>}
            </span>
          </div>
          {overCap && (
            <p className="mb-2 flex items-start gap-1 text-[11px] text-amber-700 dark:text-amber-400">
              <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
              A study's estimate exceeds the per-review cap ({fmtUsd(estimate?.budget?.per_review_cap)}). Deselect records to lower the cost, or raise the cap in settings.
            </p>
          )}
          {estimateError && !estimating && (
            <p className="mb-2 text-[11px] text-gray-400">Couldn't refresh the estimate — the review still runs; cost is enforced server-side.</p>
          )}
          <p className="mb-2.5 text-[11px] leading-snug text-gray-400 dark:text-gray-500">
            Real AI analysis that costs money. An AI-assisted pre-screening aid, not a medical or enrollment determination.
          </p>
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="rounded-lg px-3 py-2 text-sm font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-100 disabled:opacity-50 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={busy || readySelectedIds.length === 0}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition-colors duration-150 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {busy ? 'Starting…' : `Start review${readySelectedIds.length > 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** A single source toggle row (communications / forms). */
function SourceToggle({
  id, icon, title, subtitle, checked, onChange, disabled = false, disabledHint,
}: {
  id: string;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  disabledHint?: string;
}) {
  return (
    <div className={`flex items-center gap-2.5 rounded-lg border p-2.5 transition-colors duration-150 ${
      disabled
        ? 'border-gray-200 bg-gray-50 opacity-70 dark:border-gray-700 dark:bg-gray-800/60'
        : checked
          ? 'border-primary-300 bg-primary-50/50 dark:border-primary-700 dark:bg-primary-900/10'
          : 'border-gray-200 dark:border-gray-700'
    }`}>
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700"
      />
      {icon}
      <label htmlFor={id} className={`min-w-0 flex-1 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
        <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">{title}</span>
        <span className="block text-[11px] text-gray-500 dark:text-gray-400">
          {disabled && disabledHint ? disabledHint : subtitle}
        </span>
      </label>
    </div>
  );
}
